import type { Skill } from '@clockwise/clients/skills';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { SearchInput } from '@/components/custom/search-input';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { skillsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { warnIfTruncated } from '@/lib/warn-if-truncated';

// Multi-select skills picker. Emits the selected skills; caller calls the
// backend bulk-add endpoint. Skills already assigned to the user are shown
// as "already on user" and not re-selectable.
interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (skills: Skill[]) => void;
  alreadyAddedIds?: Set<string>;
}

export function BulkAddSkillsSheet({ open, onOpenChange, onSubmit, alreadyAddedIds }: Props) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<Record<string, Skill>>({});

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelected({});
    }
  }, [open]);

  useEffect(() => {
    const h = setTimeout(() => setDebounced(search), 200);
    return () => clearTimeout(h);
  }, [search]);

  const query = useQuery({
    queryKey: queryKeys.skills.list({
      q: debounced || undefined,
      active: true,
      sortField: 'name',
      sortDirection: 'asc',
      limit: 100,
      offset: 0,
    }),
    queryFn: () =>
      skillsApi.list({
        q: debounced || undefined,
        active: true,
        sortField: 'name',
        sortDirection: 'asc',
        limit: 100,
        offset: 0,
      }),
    placeholderData: keepPreviousData,
    enabled: open,
  });
  warnIfTruncated(query.data, 'bulk-add skill picker');

  const skills = query.data?.data ?? [];
  const selectedCount = Object.keys(selected).length;

  const toggle = (s: Skill) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[s.id]) delete next[s.id];
      else next[s.id] = s;
      return next;
    });
  };

  const visibleSelectableIds = skills.filter((s) => !alreadyAddedIds?.has(s.id)).map((s) => s.id);
  const allVisibleSelected =
    visibleSelectableIds.length > 0 && visibleSelectableIds.every((id) => selected[id] != null);

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = { ...prev };
      if (allVisibleSelected) {
        for (const id of visibleSelectableIds) delete next[id];
      } else {
        for (const s of skills) {
          if (!alreadyAddedIds?.has(s.id)) next[s.id] = s;
        }
      }
      return next;
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="border-b">
          <SheetTitle>Add skills</SheetTitle>
          <SheetDescription>Pick one or more skills to grant to this user.</SheetDescription>
        </SheetHeader>

        <div className="flex items-center gap-3 border-b p-3">
          <SearchInput
            placeholder="Search name, description…"
            value={search}
            onValueChange={setSearch}
          />
          <span className="ml-auto text-xs text-muted-foreground">
            {query.isFetching
              ? 'Loading…'
              : `${selectedCount} of ${query.data?.meta.total ?? 0} selected`}
          </span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    aria-label="Select all on page"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    className="size-4"
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`skel-${i}`}>
                    {Array.from({ length: 3 }).map((__, j) => (
                      <TableCell key={`skel-${i}-${j}`}>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : skills.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="py-8 text-center text-sm text-muted-foreground">
                    No skills match.
                  </TableCell>
                </TableRow>
              ) : (
                skills.map((s) => {
                  const isAdded = alreadyAddedIds?.has(s.id);
                  const isSelected = selected[s.id] != null;
                  return (
                    <TableRow
                      key={s.id}
                      className={cn(!isAdded && 'cursor-pointer', isAdded && 'opacity-50')}
                      onClick={() => {
                        if (!isAdded) toggle(s);
                      }}
                    >
                      <TableCell>
                        <input
                          type="checkbox"
                          aria-label={`Select ${s.name}`}
                          checked={isSelected}
                          disabled={isAdded}
                          onChange={() => toggle(s)}
                          onClick={(e) => e.stopPropagation()}
                          className="size-4"
                        />
                      </TableCell>
                      <TableCell>
                        {s.name}
                        {isAdded ? (
                          <span className="ml-2 text-xs text-muted-foreground">
                            (already on user)
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {s.description ?? '—'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        <SheetFooter className="flex-row justify-end border-t">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={selectedCount === 0}
            onClick={() => {
              onSubmit(Object.values(selected));
              onOpenChange(false);
            }}
          >
            Add {selectedCount > 0 ? `${selectedCount} ` : ''}skill{selectedCount === 1 ? '' : 's'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
